import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Star, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { SiteNav } from '@/components/SiteNav';
import { SiteFooter } from '@/components/SiteFooter';
import { getProducts } from '@/lib/store';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/hooks/useToast';

const categoryMeta: Record<string, { title: string; description: string; emoji: string }> = {
  hunting: { title: 'Hunting', description: 'Everything you need to take the field with confidence — from optics and firearms to blinds, calls, and decoys.', emoji: '🦌' },
  fishing: { title: 'Fishing', description: 'Rods, reels, tackle, and ice fishing gear built for Dakota lakes and rivers.', emoji: '🎣' },
  archery: { title: 'Archery', description: 'Compound bows, crossbows, arrows, and precision archery accessories.', emoji: '🏹' },
  apparel: { title: 'Apparel', description: 'Field-ready camo, performance base layers, waterproof outerwear, and rugged boots.', emoji: '🧥' },
  camping: { title: 'Camping', description: 'Tents, packs, knives, fire starters, and essential survival gear for the backcountry.', emoji: '⛺' },
  accessories: { title: 'Accessories', description: 'Trail cameras, calls, packs, and everything else to complete your kit.', emoji: '🔦' },
};

export function CategoryPage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '').toLowerCase();
  const meta = categoryMeta[slug];
  const allProducts = getProducts();
  const products = allProducts.filter((p) => p.category === slug && p.inStock);
  const { addToCart } = useCart();
  const { toast } = useToast();

  useSeoMeta({
    title: meta ? `${meta.title} Gear — Dakota Horizon Outdoors` : 'Category — Dakota Horizon Outdoors',
    description: meta?.description ?? 'Premium outdoor gear.',
  });

  function handleAddToCart(productId: string, name: string) {
    addToCart(productId);
    toast({ title: `${name} added to cart!` });
  }

  if (!meta) {
    return (
      <div className="min-h-screen bg-background">
        <SiteNav />
        <main className="pt-24 container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">Category Not Found</h1>
          <Link to="/" className="text-amber-600 hover:underline">← Back to Home</Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      {/* Header */}
      <section className="pt-16 pb-10 bg-[hsl(25,20%,8%)]">
        <div className="container mx-auto px-4 pt-8">
          <Link to="/" className="inline-flex items-center gap-1 text-stone-400 hover:text-amber-400 text-sm mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-4xl sm:text-5xl">{meta.emoji}</span>
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">{meta.title}</h1>
              <p className="text-stone-400 mt-1 text-sm sm:text-base max-w-2xl">{meta.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <main className="py-10 sm:py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <p className="text-muted-foreground text-sm">{products.length} product{products.length !== 1 ? 's' : ''}</p>
          </div>

          {products.length === 0 ? (
            <Card className="border-dashed max-w-sm mx-auto">
              <CardContent className="py-16 text-center">
                <p className="text-muted-foreground">No products in this category yet.</p>
                <p className="text-muted-foreground text-sm mt-1">Check back soon or visit the admin to add products.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden product-card-hover border-border/50 bg-card group">
                  {/* Product image */}
                  <div className="aspect-square bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-800 dark:to-stone-900 relative flex items-center justify-center overflow-hidden">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <ImageIcon className="h-10 w-10 text-stone-300 dark:text-stone-600" />
                      </div>
                    )}
                    {product.badge && (
                      <span className="absolute top-2 left-2 bg-amber-600 text-white text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-sm">
                        {product.badge}
                      </span>
                    )}
                  </div>

                  <CardContent className="p-3 sm:p-4">
                    <h3 className="font-semibold text-foreground text-xs sm:text-sm leading-snug mb-2 line-clamp-2">{product.name}</h3>
                    <div className="flex items-center justify-between mt-auto">
                      <div>
                        <span className="font-bold text-foreground text-sm sm:text-base">${product.price.toFixed(2)}</span>
                        {product.originalPrice && (
                          <span className="text-muted-foreground text-xs line-through ml-1.5">${product.originalPrice.toFixed(2)}</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(product.id, product.name)}
                        className="bg-amber-700 hover:bg-amber-600 text-white text-xs rounded-sm h-7 sm:h-8 px-2 sm:px-3"
                      >
                        <ShoppingCart className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Add</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default CategoryPage;
